import { ApiClient } from './api-client'
import { useAuthStore } from '@/stores/auth'

/**
 * CLIENT-PILOT W5A — eligibility is declared PER ENDPOINT, next to the endpoint.
 *
 * The preload is shared: the auth store fires it one second after a real login for every role it does not
 * call "operational". Measured on the composed pilot (W6): for `department_head` two of its three requests
 * were refused on every login — `/analytics/kpis` is scope-denied for a role that holds a membership and not
 * a group grant, and `/users/profile` is vendor-only and outside the signed pilot surface. Both refusals
 * were swallowed below, so nothing broke and nothing was disclosed; the client simply asked, on a sold
 * role's behalf, for things that role is never given.
 *
 * `notForRoles` is NOT authorization — the server stays the only authority, and an ineligible request
 * would still be refused. It is the client declining to ask. The role predicates of the auth store
 * (`isOperationalRole`, …) are deliberately not touched: they mean other things to other surfaces.
 */
type PreloadEndpoint = { method: 'GET'; url: string; params?: Record<string, unknown>; notForRoles?: readonly string[] }

export const PRELOAD_ENDPOINTS: readonly PreloadEndpoint[] = Object.freeze([
  // ⚠️ REMOVED: '/incidences' - Department Head no tiene acceso a este endpoint general
  // Department Head debe usar: '/department-head/incidences'
  // { method: 'GET', url: '/incidences', params: { status: 'active' } },
  { method: 'GET', url: '/analytics/kpis', notForRoles: ['department_head'] },
  { method: 'GET', url: '/users/profile', notForRoles: ['department_head'] },
  { method: 'GET', url: '/dashboard/summary' }
])

/**
 * The endpoints the preload may ask on behalf of `role`. NO role, NO preload: the timer fires a second after
 * login, and a session that ended inside that second has nobody to preload for — asking anyway would send three
 * unauthenticated requests into the 401 handling of a session that is already gone.
 */
export function preloadEndpointsFor(role: string | null | undefined): readonly PreloadEndpoint[] {
  if (!role) return []
  return PRELOAD_ENDPOINTS.filter((e) => !e.notForRoles?.includes(role))
}

// Preload de endpoints críticos tras autenticación usando el ApiClient TS
export async function preloadCriticalData(): Promise<void> {
  // The role is read where it is owned, at call time (the same call-time cycle `websocket.service` has
  // with the store). If it cannot be read there is no one to preload for.
  let role: string | null = null
  try { role = useAuthStore().userRole ?? null } catch { role = null }

  await Promise.allSettled(
    preloadEndpointsFor(role).map(async (e) => {
      try {
        if (e.method === 'GET') {
          await ApiClient.get(e.url, e.params)
        }
      } catch {
        // Ignorar fallos en precarga
      }
    })
  )
}
